// Warm-up for Apryse's static assets. Everything listed here is identical for every drawing and
// every quotation — only the PDF itself is per-drawing — but none of it is fetched until the
// viewer is constructed, which `useQuotationViewer` only does once the editor modal is already
// open. A cold editor open therefore pays ~9 MB (gzipped) of bundles + wasm + fonts AFTER the
// click. Pulling the same files into the HTTP cache while the user is doing something else takes
// that download off the critical path; the browser then serves them from cache when the viewer
// actually boots.
//
// This is a cache warm-up, not a correctness-critical path: every request is best-effort and a
// 404 (e.g. after an Apryse upgrade renames a file) only means that one file is fetched later,
// the way it is today. Pair it with the `Cache-Control` window that `public/_headers` sets on
// `/webviewer/*` — without a cacheable response there is nothing for the warm-up to leave behind.

// Matches the `path` option in webviewerInit.ts and the copy destination in
// tools/copyStaticAssets.ts.
const WEBVIEWER_PATH = "/webviewer"

// The core + UI bundles and the PDF resource blob, needed by every boot regardless of document.
const SHARED_ASSETS = [
	`${WEBVIEWER_PATH}/core/webviewer-core.min.js`,
	`${WEBVIEWER_PATH}/ui/webviewer-ui.min.js`,
	`${WEBVIEWER_PATH}/core/pdf/pdfnet.res`,
	`${WEBVIEWER_PATH}/ui/i18n/translation-en.json`,
]

// Apryse's UI loads all of these on mount through `new FontFace(...).load()` — the ten
// content-edit substitution families plus the signature fonts. It is hard-coded in
// webviewer-ui.min.js with no option to disable, so ~2 MB (gzipped) is spent on fonts on every
// cold boot whether or not anything renders them. Warming them is the only way to stop that
// competing for bandwidth with the wasm worker and the PDF.
const FONT_ASSETS = [
	"webfonts/Arimo.ttf",
	"webfonts/Caladea.ttf",
	"webfonts/Carlito.ttf",
	"webfonts/Cousine.ttf",
	"webfonts/LiberationSerif.ttf",
	"webfonts/NotoSansArabic.ttf",
	"webfonts/OpenSans.ttf",
	"webfonts/Roboto.ttf",
	"webfonts/RobotoMono.ttf",
	"webfonts/Tinos.ttf",
	"Lato-Regular.woff",
	"Lato-Bold.woff",
	"Lato-Italic.woff",
	"Lato-BoldItalic.woff",
	"GreatVibes-Regular.woff2",
	"La-Belle-Aurore.woff",
	"Nothing-You-Could-Do.woff",
	"Satisfy.woff",
	"Whisper.woff",
].map((font) => `${WEBVIEWER_PATH}/ui/assets/fonts/${font}`)

// Which wasm build Apryse picks depends on cross-origin isolation, NOT on our own options: with
// COOP/COEP in place (public/_headers in production, `server.headers` in the Vite dev server) it
// loads the threaded build, otherwise the single-threaded one. Warming the wrong variant would
// download ~2.8 MB that the viewer never asks for, so mirror the same condition here.
const THREADED_WASM_ASSETS = [
	`${WEBVIEWER_PATH}/core/pdf/lean/optimized/PDFNetThreadedWasm.br.wasm`,
	`${WEBVIEWER_PATH}/core/pdf/lean/optimized/PDFNetThreadedWasm.br.js.mem`,
	`${WEBVIEWER_PATH}/core/pdf/lean/optimized/PDFNetThreadedWasm.worker.js`,
]

const SINGLE_THREAD_WASM_ASSETS = [
	`${WEBVIEWER_PATH}/core/pdf/lean/optimized/PDFNetCWasm.br.wasm`,
	`${WEBVIEWER_PATH}/core/pdf/lean/optimized/PDFNetCWasm.br.js.mem`,
	`${WEBVIEWER_PATH}/core/pdf/lean/optimized/PDFNetCWasm.js`,
]

// Connection types on which a multi-megabyte speculative download would cost the user more than
// the editor open saves them.
const SLOW_CONNECTION_TYPES = ["slow-2g", "2g", "3g"]

type TNetworkInformation = {
	saveData?: boolean
	effectiveType?: string
}

/**
 * The asset list to warm, in the order it should be fetched: the bundles and wasm the viewer
 * blocks on first, then the fonts it loads asynchronously once its UI mounts.
 */
export const getViewerPreloadAssets = (isCrossOriginIsolated: boolean): string[] => [
	...SHARED_ASSETS,
	...(isCrossOriginIsolated ? THREADED_WASM_ASSETS : SINGLE_THREAD_WASM_ASSETS),
	...FONT_ASSETS,
]

/**
 * Whether a speculative download is appropriate at all — Data Saver and slow connections opt out.
 */
export const shouldPreloadViewerAssets = (): boolean => {
	const connection = (navigator as Navigator & { connection?: TNetworkInformation }).connection
	if (!connection) return true
	if (connection.saveData) return false
	return !SLOW_CONNECTION_TYPES.includes(connection.effectiveType ?? "")
}

// One warm-up per page load. The guard is module-level rather than component-level so a remount
// (or a second caller, e.g. the Documents-tab viewer) can't start it again.
let hasStarted = false

/**
 * Fetch every shared viewer asset into the HTTP cache. Resolves once the last one settles;
 * rejections are swallowed, so callers can fire and forget.
 */
export const preloadViewerAssets = async (): Promise<void> => {
	if (hasStarted) return
	// The connection check runs BEFORE the latch is set, not after. Latching first meant one call
	// made while Data-Saver was on (or on a 2g/3g reading) disabled the preload for the whole page
	// session, even once the connection recovered — the caller would return early forever on a
	// decision taken from a single sample. Now a declined attempt leaves the door open. (PR #1429
	// review.)
	if (!shouldPreloadViewerAssets()) return
	hasStarted = true

	const assets = getViewerPreloadAssets(Boolean(window.crossOriginIsolated))

	// Sequential on purpose: this is speculative work, and 20 parallel requests would contend with
	// the app's own API calls for connections. `priority: "low"` is Chromium-only (ignored
	// elsewhere) and `force-cache` means an asset already in the cache costs no network at all.
	const init = { cache: "force-cache", priority: "low" } as RequestInit

	for (const asset of assets) {
		try {
			const response = await fetch(asset, init)
			// The body must be drained for the response to be committed to the HTTP cache — an
			// unread body can be cancelled before the download finishes.
			await response.arrayBuffer()
		} catch {
			// Warm-up only: the viewer will fetch this file itself when it boots.
		}
	}
}
